import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Footer from "../../components/ui/Footer";
import Menu from "../../components/ui/Menu";
import Navbar from "../../components/ui/Navbar";
import RewardProduct from "../../components/rewards/RewardProduct";
import { getCurrentGroup } from "./rewardData";
import useRewardsProgramme from "./useRewardsProgramme";
import styles from "./Rewards.module.css";

const ArrowIcon = () => (
  <svg className={styles.arrowIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path d="m9 5 7 7-7 7" />
  </svg>
);

function Rewards({ showMenu, setShowMenu }) {
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const activeGroupRef = useRef(null);
  const lastAutoScrolledLocationRef = useRef(null);
  const location = useLocation();
  const token = useSelector((state) => state.userData.loginData.token);
  const { groups, policyCount, isLoading } = useRewardsProgramme(token);
  const currentGroup = getCurrentGroup(groups, policyCount);
  const hasPolicyCount = Number.isFinite(policyCount);
  const ascendingGroups = [...groups].sort((first, second) =>
    first.id.localeCompare(second.id),
  );

  useEffect(() => {
    setHasUserSelected(false);
  }, [location.key]);

  useEffect(() => {
    if (hasUserSelected) return;

    if (currentGroup) {
      setSelectedGroupId(currentGroup.id);
    } else if (!isLoading && groups.length) {
      setSelectedGroupId(groups[groups.length - 1].id);
    }
  }, [currentGroup, groups, hasUserSelected, isLoading]);

  useEffect(() => {
    if (
      isLoading ||
      !selectedGroupId ||
      hasUserSelected ||
      lastAutoScrolledLocationRef.current === location.key
    ) {
      return undefined;
    }

    let firstFrame;
    let secondFrame;
    const scrollTimer = window.setTimeout(() => {
      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => {
          const activeGroup = activeGroupRef.current;
          if (!activeGroup) return;

          const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
          activeGroup.scrollIntoView({
            behavior: reduceMotion ? "auto" : "smooth",
            block: "center",
            inline: "nearest",
          });
          lastAutoScrolledLocationRef.current = location.key;
        });
      });
    }, 220);

    return () => {
      window.clearTimeout(scrollTimer);
      if (firstFrame) window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [hasUserSelected, isLoading, location.key, selectedGroupId]);

  const selectedGroup = groups.find((group) => group.id === selectedGroupId) || null;
  const selectedIsCurrent = selectedGroup?.id === currentGroup?.id;

  const getSelectedMessage = () => {
    if (!selectedGroup || !hasPolicyCount) {
      return "Keep going — this reward is within reach!";
    }

    if (selectedIsCurrent) {
      return `Great work — you made it to ${selectedGroup.name}!`;
    }

    const remainingToSelected = Math.max(selectedGroup.minimumPolicies - policyCount, 0);
    if (remainingToSelected > 0) {
      const policyWord = remainingToSelected === 1 ? "policy" : "policies";
      return `${remainingToSelected.toLocaleString()} more ${policyWord} to reach ${selectedGroup.name} — keep going!`;
    }

    return `You have passed ${selectedGroup.name} — keep pushing!`;
  };

  const selectGroup = (groupId) => {
    setHasUserSelected(true);
    setSelectedGroupId(groupId);
  };

  return (
    <div>
      <div className="menu" onClick={() => setShowMenu(false)}>
        {showMenu && <Menu setShowMenu={setShowMenu} />}
      </div>
      <Navbar title="Rewards" setShowMenu={setShowMenu} />

      <main className={styles.rewardsBody}>
        <section className={styles.programIntro}>
          <p className={styles.eyebrow}></p>
          <h1>Agent rewards programme</h1>
          <p>Every qualifying policy brings you closer to a bigger reward.</p>
        </section>

        {/* <section className={styles.programmeHeader}>
          <div className={styles.programmeCampaignMark}>
            <img src={CAMPAIGN_IMAGE} alt="Transact More, Get More" />
          </div>

          <div className={styles.programmeHeaderCopy}>
            <p>{currentGroup?.name || "Rewards programme"}</p>
            <h2>
              {isLoading
                ? "Checking your qualification..."
                : hasPolicyCount
                  ? `${policyCount.toLocaleString()} points`
                  : "Progress unavailable"}
            </h2>
            <span>
              {currentGroup
                ? currentGroup.reward
                : hasPolicyCount
                  ? "Issue 50 policies to enter Group G."
                  : error || "Your reward group will appear when progress is available."}
            </span>
          </div>

          <img
            className={styles.programmeCombinedGift}
            src={REWARDS_HEADER_IMAGE}
            alt="Agent rewards gift collection"
          />
        </section> */}

        <section className={styles.rewardsSection}>
          <div className={styles.sectionHeading}>
            <div>
              <p className={styles.eyebrow}>Reward groups</p>
              {/* <h2>What you can earn</h2> */}
            </div>
          </div>

          <div className={styles.tierList}>
            {ascendingGroups.map((group) => {
              const selected = group.id === selectedGroup?.id;

              if (selected) {
                return (
                  <article
                    key={group.id}
                    ref={activeGroupRef}
                    className={`${styles.tierCard} ${styles.tierActive}`}
                  >
                    <div className={styles.activeTierVisual}>
                      <RewardProduct
                        tier={group}
                        showPolicyRange
                        className={styles.activeTierProduct}
                      />
                    </div>

                    <div className={styles.activeTierContent}>
                      <p className={styles.activeTierSummary}>
                        {selectedIsCurrent && hasPolicyCount && (
                          <>
                            <strong>{policyCount.toLocaleString()} points.</strong>{" "}
                          </>
                        )}
                        <strong>{group.reward}.</strong>{" "}
                        {getSelectedMessage()}
                      </p>
                    </div>
                  </article>
                );
              }

              return (
                <button
                  key={group.id}
                  type="button"
                  className={`${styles.tierCard} ${styles.tierInactive}`}
                  aria-label={`Show details for ${group.name}`}
                  onClick={() => selectGroup(group.id)}
                >
                  <RewardProduct
                    tier={group}
                    compact
                    className={styles.tierProduct}
                  />
                  <div className={styles.tierContent}>
                    <div className={styles.tierTitleRow}>
                      <h3>{group.name}</h3>
                    </div>
                    <div className={styles.tierDetails}>
                      <div className={styles.tierDetailsInner}>
                        <p>{group.reward}</p>
                      </div>
                    </div>
                  </div>
                  <span className={styles.tierCardEnd}>
                    <span className={styles.tierRange}>
                      <strong>
                        {group.minimumPolicies.toLocaleString()}–{group.maximumPolicies.toLocaleString()}
                      </strong>
                      <small>policies</small>
                    </span>
                    <span className={styles.tierExpandButton} aria-hidden="true">
                      <ArrowIcon />
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default Rewards;
